import type { CaregiverRole, InvitationStatus } from '@/types';

export type InviteeOnboardingFlow = 'invitee-editor' | 'invitee-viewer';

export interface InvitationOnboardingContext {
  id: string;
  role: Exclude<CaregiverRole, 'PRIMARY'>;
  status: InvitationStatus;
  expiresAt: string | null;
  onboardingCompletedAt: string | null;
}

export type OnboardingResolution =
  | {
      destination: 'invitee';
      flow: InviteeOnboardingFlow;
      invitationId: string;
    }
  | {
      destination: 'invite-expired';
      invitationId: string;
    }
  | {
      destination: 'invite-deferred';
      invitationId: string;
      role: 'CO_ADMIN';
    }
  | {
      destination: 'admin';
    }
  | {
      destination: 'app';
    };

interface ResolveOnboardingInput {
  invitation: InvitationOnboardingContext | null;
  adminOnboardingCompletedAt: string | null;
  now?: Date;
}

/**
 * `expires_at` bounds how long a PENDING invitation may be CONSUMED — it does
 * not bound how long an already-accepted invitee may take to finish
 * onboarding. Applying it to an ACCEPTED row evicts a real, seated member
 * from their own flow: expiry is `created_at + 60 min`, acceptance and the
 * OAuth round trip eat part of that, and `resolveOnboarding` runs on every
 * render with a fresh `now` — so the invitee gets thrown out mid-carousel.
 *
 * The server deliberately gates on PENDING in all three of its checks
 * (invitations-preview, provision_invited_user, join_household_membership).
 * This mirrors them; the two must not drift.
 */
function isExpired(
  invitation: InvitationOnboardingContext,
  now: Date,
): boolean {
  if (invitation.status === 'EXPIRED') return true;
  if (invitation.status !== 'PENDING') return false;
  if (!invitation.expiresAt) return false;

  const expiresAt = Date.parse(invitation.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= now.getTime();
}

/**
 * Resolves the onboarding experience from the explicit invitation context
 * first. A user's access level is scoped to a household, so the role of their
 * currently active membership must never decide how a different invitation
 * behaves.
 */
export function resolveOnboarding({
  invitation,
  adminOnboardingCompletedAt,
  now = new Date(),
}: ResolveOnboardingInput): OnboardingResolution {
  if (invitation) {
    if (isExpired(invitation, now)) {
      return {
        destination: 'invite-expired',
        invitationId: invitation.id,
      };
    }

    if (
      invitation.status === 'DECLINED' ||
      invitation.onboardingCompletedAt !== null
    ) {
      return { destination: 'app' };
    }

    if (invitation.role === 'CO_ADMIN') {
      return {
        destination: 'invite-deferred',
        invitationId: invitation.id,
        role: invitation.role,
      };
    }

    return {
      destination: 'invitee',
      flow: invitation.role === 'EDITOR' ? 'invitee-editor' : 'invitee-viewer',
      invitationId: invitation.id,
    };
  }

  return adminOnboardingCompletedAt === null
    ? { destination: 'admin' }
    : { destination: 'app' };
}
